import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Modal,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { toast } from "react-toastify";
import {
  APP_BACKGROUND_COLOR,
  APP_COLOR,
  APP_HOVER_COLOR,
  BORDER_COLOR,
  BUTTON_TEXT_COLOR,
  FONT_FAMILY,
} from "../../theme";
import { acceptGuestTerms } from "../../services/guestSessionService";
import Mixpanel from "../../utils/mixpanel";

const linkStyle = {
  color: APP_COLOR,
  textDecoration: "none",
  fontWeight: 600,
};

interface GuestTermsModalProps {
  open: boolean;
  onAccepted: () => void;
  onCancel: () => void;
}

const GuestTermsModal: React.FC<GuestTermsModalProps> = ({
  open,
  onAccepted,
  onCancel,
}) => {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleAccept = async () => {
    if (!checked || submitting) return;
    setSubmitting(true);
    try {
      await acceptGuestTerms();
      Mixpanel.track("guest_terms_accepted");
      setChecked(false);
      onAccepted();
    } catch (err) {
      toast.error("Could not record terms acceptance. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const trackLink = (doc: "terms" | "privacy") => () => {
    Mixpanel.track("guest_terms_link_clicked", { doc });
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!submitting) onCancel();
      }}
      disableEscapeKeyDown
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          backgroundColor: APP_BACKGROUND_COLOR,
          border: `1px solid ${BORDER_COLOR}`,
          borderRadius: "16px",
          boxShadow: 24,
          width: { xs: "94vw", sm: "520px" },
          maxHeight: "90vh",
          overflowY: "auto",
          padding: { xs: "26px 22px", sm: "30px" },
          fontFamily: FONT_FAMILY,
        }}
      >
        <Typography
          sx={{
            color: "white",
            fontFamily: FONT_FAMILY,
            fontWeight: "bold",
            fontSize: { xs: "22px", sm: "26px" },
            lineHeight: 1.2,
            mb: 1.5,
          }}
        >
          Before your first upload
        </Typography>
        <Typography
          sx={{
            color: "rgba(255,255,255,0.82)",
            fontSize: { xs: "14.5px", sm: "15.5px" },
            lineHeight: 1.7,
            mb: 2.5,
          }}
        >
          Ommuse asks every guest to review the Terms of Use and Privacy
          Policy once, before uploading. You only need to do this on this
          session.
        </Typography>
        <FormControlLabel
          sx={{
            alignItems: "flex-start",
            mb: 2.5,
            color: "white",
            "& .MuiFormControlLabel-label": {
              fontSize: "14.5px",
              lineHeight: 1.65,
            },
          }}
          control={
            <Checkbox
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              sx={{
                color: APP_COLOR,
                pt: "3px",
                "&.Mui-checked": { color: APP_COLOR },
              }}
            />
          }
          label={
            <span>
              I agree to the{" "}
              <a
                href={`${window.location.origin}/terms-of-use`}
                target="_blank"
                rel="noopener noreferrer"
                style={linkStyle}
                onClick={trackLink("terms")}
              >
                Terms of Use
              </a>{" "}
              and{" "}
              <a
                href={`${window.location.origin}/privacy-policy`}
                target="_blank"
                rel="noopener noreferrer"
                style={linkStyle}
                onClick={trackLink("privacy")}
              >
                Privacy Policy
              </a>
              .
            </span>
          }
        />
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            onClick={onCancel}
            disabled={submitting}
            sx={{
              flex: "0 0 auto",
              color: "rgba(255,255,255,0.78)",
              fontFamily: FONT_FAMILY,
              fontWeight: 600,
              textTransform: "none",
              fontSize: "15px",
              borderRadius: "10px",
              padding: "10px 18px",
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            disabled={submitting || !checked}
            onClick={handleAccept}
            sx={{
              backgroundColor: APP_COLOR,
              color: BUTTON_TEXT_COLOR,
              fontFamily: FONT_FAMILY,
              fontWeight: "bold",
              textTransform: "none",
              fontSize: "15.5px",
              borderRadius: "10px",
              padding: "10px",
              "&:hover": { backgroundColor: APP_HOVER_COLOR },
              "&.Mui-disabled": {
                color: `${BUTTON_TEXT_COLOR}80`,
                backgroundColor: `${APP_COLOR}60`,
              },
            }}
          >
            {submitting ? (
              <CircularProgress size={18} sx={{ color: BUTTON_TEXT_COLOR }} />
            ) : (
              "Agree and continue"
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default GuestTermsModal;
